import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { shopifyAPI } from '../services/api';
import { format, endOfYear, subYears } from 'date-fns';
import { useTenantUrl } from '../hooks/useTenantUrl';

const UnpaidReport: React.FC = () => {
  const { tenantNavigate } = useTenantUrl();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [allOrders, setAllOrders] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>(null);
  
  // Get default date (last day of last year)
  const defaultDate = format(endOfYear(subYears(new Date(), 1)), 'yyyy-MM-dd');
  
  // Read filters from URL params, with defaults
  const urlDate = searchParams.get('date') || defaultDate;
  const urlStatus = searchParams.get('status') || '';
  const urlShipped = searchParams.get('shipped') || '';
  
  // Local state for inputs (allows typing without URL updates on every keystroke)
  const [dateInput, setDateInput] = useState(urlDate);
  const [statusFilter, setStatusFilter] = useState(urlStatus);
  const [shippedFilter, setShippedFilter] = useState(urlShipped);
  
  // The date used for fetching (from URL)
  const asOfDate = urlDate;
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  // Sync local state when URL changes (e.g., browser back/forward)
  useEffect(() => {
    setDateInput(urlDate);
    setStatusFilter(urlStatus);
    setShippedFilter(urlShipped);
  }, [urlDate, urlStatus, urlShipped]);

  // Update URL params helper
  const updateParams = useCallback((updates: { date?: string; status?: string; shipped?: string }) => {
    const newParams = new URLSearchParams(searchParams);
    
    if (updates.date !== undefined) {
      if (updates.date && updates.date !== defaultDate) {
        newParams.set('date', updates.date);
      } else {
        newParams.delete('date');
      }
    }
    
    if (updates.status !== undefined) {
      if (updates.status) {
        newParams.set('status', updates.status);
      } else {
        newParams.delete('status');
      }
    }
    
    if (updates.shipped !== undefined) {
      if (updates.shipped) {
        newParams.set('shipped', updates.shipped);
      } else {
        newParams.delete('shipped');
      }
    }
    
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams, defaultDate]);

  // Fetch report on initial load only
  useEffect(() => {
    fetchReport();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchReport = async (date?: string, shipped?: string) => {
    try {
      setLoading(true);
      setError(null);

      const shippedParam = (shipped ?? shippedFilter) as 'shipped' | 'unshipped' | undefined;
      const response = await shopifyAPI.getUnpaidOrders(
        date || dateInput, 
        shippedParam || undefined
      );
      setAllOrders(response.data.data);
      setSummary(response.data.summary);
      setCurrentPage(1); // Reset to first page on new fetch
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load unpaid orders');
    } finally {
      setLoading(false);
    }
  };

  // Get unique statuses for filter dropdown
  const availableStatuses = useMemo(() => {
    const statuses = new Set<string>();
    allOrders.forEach(order => {
      if (order.financial_status) {
        statuses.add(order.financial_status);
      }
    });
    return Array.from(statuses).sort();
  }, [allOrders]);

  // Filter orders based on selected status
  const filteredOrders = useMemo(() => {
    if (!statusFilter) return allOrders;
    return allOrders.filter(order => order.financial_status === statusFilter);
  }, [allOrders, statusFilter]);

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDateInput(e.target.value);
  };

  const handleDateBlur = () => {
    // Update URL when user finishes typing
    if (dateInput !== urlDate) {
      updateParams({ date: dateInput });
    }
  };

  const handleStatusChange = (newStatus: string) => {
    setStatusFilter(newStatus);
    updateParams({ status: newStatus });
    setCurrentPage(1);
  };

  const handleShippedChange = (newShipped: string) => {
    setShippedFilter(newShipped);
    updateParams({ shipped: newShipped });
    // Refetch with new filter
    fetchReport(dateInput, newShipped);
  };

  const handleRefresh = () => {
    // Sync URL with current input values and fetch
    updateParams({ date: dateInput, status: statusFilter, shipped: shippedFilter });
    fetchReport(dateInput, shippedFilter);
  };

  const handleExport = () => {
    const shippedParam = shippedFilter as 'shipped' | 'unshipped' | undefined;
    shopifyAPI.exportUnpaidOrders(dateInput, statusFilter || undefined, shippedParam || undefined);
  };

  const handleClearFilters = () => {
    setStatusFilter('');
    setShippedFilter('');
    setDateInput(defaultDate);
    updateParams({ date: '', status: '', shipped: '' });
    setCurrentPage(1);
    fetchReport(defaultDate, '');
  };

  // Pagination calculations
  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedOrders = filteredOrders.slice(startIndex, endIndex);

  // Reset page when status filter changes via URL
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter]);

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  if (loading) {
    return <div className="loading">Loading unpaid orders...</div>;
  }

  const hasActiveFilters = !!statusFilter || !!shippedFilter;

  // Calculate which columns to show based on shipped filter
  const showShippedColumn = shippedFilter !== 'unshipped';
  const showDaysSinceShipColumn = shippedFilter === 'shipped';

  return (
    <div>
      <div className="page-header">
        <h1>Unpaid Orders (by date)</h1>
        <p>Orders that were not fully paid as of the selected date</p>
      </div>

      {/* Filter Card */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '1rem',
          alignItems: 'end'
        }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label htmlFor="asOfDate" style={{ fontSize: '0.875rem', fontWeight: 500, color: '#4a5568' }}>
              As Of Date
            </label>
            <input
              type="date"
              id="asOfDate"
              className="form-control"
              value={dateInput}
              onChange={handleDateChange}
              onBlur={handleDateBlur}
              style={{ width: '100%' }}
            />
          </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label htmlFor="shippedFilter" style={{ fontSize: '0.875rem', fontWeight: 500, color: '#4a5568' }}>
              Fulfillment Status
            </label>
            <select
              id="shippedFilter"
              className="form-control"
              value={shippedFilter}
              onChange={(e) => handleShippedChange(e.target.value)}
              style={{ width: '100%' }}
            >
              <option value="">All Orders</option>
              <option value="shipped">Shipped Only</option>
              <option value="unshipped">Unshipped Only</option>
            </select>
          </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label htmlFor="statusFilter" style={{ fontSize: '0.875rem', fontWeight: 500, color: '#4a5568' }}>
              Payment Status
            </label>
            <select
              id="statusFilter"
              className="form-control"
              value={statusFilter}
              onChange={(e) => handleStatusChange(e.target.value)}
              style={{ width: '100%' }}
            >
              <option value="">All Statuses</option>
              {availableStatuses.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button className="btn btn-primary" onClick={handleRefresh}>
              Refresh
            </button>
            {hasActiveFilters && (
              <button className="btn btn-secondary" onClick={handleClearFilters}>
                Clear Filters
              </button>
            )}
            {filteredOrders.length > 0 && (
              <button className="btn btn-secondary" onClick={handleExport}>
                📥 Export CSV
              </button>
            )}
          </div>
        </div>

        {hasActiveFilters && (
          <div style={{ 
            marginTop: '1rem', 
            padding: '0.75rem', 
            backgroundColor: '#ebf8ff', 
            borderRadius: '0.375rem',
            fontSize: '0.875rem',
            color: '#2b6cb0'
          }}>
            Showing {filteredOrders.length} of {allOrders.length} orders
            {shippedFilter && <span> • Fulfillment: <strong>{shippedFilter}</strong></span>}
            {statusFilter && <span> • Status: <strong>{statusFilter}</strong></span>}
          </div>
        )}
      </div>

      {error && <div className="error">{error}</div>}

      {/* Summary Stats */}
      {summary && (
        <div className="stat-grid" style={{ marginBottom: '1.5rem' }}>
          <div className="stat-card">
            <h3>Unpaid Orders</h3>
            <div className="value" style={{ color: '#e53e3e' }}>
              {summary.total_count}
            </div>
            <div className="subvalue">
              Not fully paid
            </div>
          </div>

          <div className="stat-card">
            <h3>Total Outstanding</h3>
            <div className="value" style={{ color: '#e53e3e' }}>
              ${Number(summary.total_outstanding || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <div className="subvalue">
              Open balance (after partial payments &amp; returns)
            </div>
          </div>

          <div className="stat-card">
            <h3>Shipped</h3>
            <div className="value" style={{ color: '#38a169' }}>
              {summary.shipped_count || 0}
            </div>
            <div className="subvalue">
              Already fulfilled
            </div>
          </div>

          <div className="stat-card">
            <h3>Unshipped</h3>
            <div className="value" style={{ color: '#d69e2e' }}>
              {summary.unshipped_count || 0}
            </div>
            <div className="subvalue">
              Not yet fulfilled
            </div>
          </div>
        </div>
      )}

      {/* Orders Table */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2>Unpaid Orders {filteredOrders.length > 0 && <span style={{ fontWeight: 400, fontSize: '1rem', color: '#718096' }}>({filteredOrders.length} total)</span>}</h2>
          {filteredOrders.length > itemsPerPage && (
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <span style={{ color: '#718096', fontSize: '0.9rem' }}>
                Page {currentPage} of {totalPages}
              </span>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  className="btn btn-secondary"
                  onClick={handlePreviousPage}
                  disabled={currentPage === 1}
                  style={{ padding: '0.5rem 1rem' }}
                >
                  Previous
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={handleNextPage}
                  disabled={currentPage >= totalPages}
                  style={{ padding: '0.5rem 1rem' }}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
        
        {filteredOrders.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#38a169' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✓</div>
            <div style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>
              {hasActiveFilters ? 'No orders match your filters' : 'No unpaid orders!'}
            </div>
            <div style={{ marginTop: '0.5rem', color: '#718096' }}>
              {hasActiveFilters 
                ? 'Try adjusting your filter criteria'
                : `All orders were fully paid by ${format(new Date(asOfDate), 'MMMM dd, yyyy')}`
              }
            </div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Order #</th>
                  <th>Order Date</th>
                  <th>Customer</th>
                  <th>Order Total</th>
                  <th>Returns by Date</th>
                  <th>Effective Total</th>
                  <th>Paid by Date</th>
                  <th>Paid Date</th>
                  {showShippedColumn && <th>Shipped Date</th>}
                  {showDaysSinceShipColumn && <th>Days Since Ship</th>}
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {paginatedOrders.map((order: any) => {
                  const shipDate = order.fulfillment_date ? new Date(order.fulfillment_date) : null;
                  const daysOld = shipDate 
                    ? Math.floor((new Date(asOfDate).getTime() - shipDate.getTime()) / (1000 * 60 * 60 * 24))
                    : 0;
                  const isOverdue = shipDate && daysOld > 30;
                  const paymentsAsOfDate = Number(order.payments_as_of_date || 0);
                  const totalAmount = Number(order.total_amount || 0);
                  // Use date-aware refund amount if available, fallback to total refund_amount
                  const refundAmountAsOfDate = Number(order.refund_amount_as_of_date ?? order.refund_amount ?? 0);
                  // Calculate effective total using the date-aware refund amount
                  const effectiveTotal = totalAmount - refundAmountAsOfDate;
                  const hasPartialPayment = paymentsAsOfDate > 0;
                  const hasReturns = refundAmountAsOfDate > 0;
                  
                  return (
                    <tr key={order.id} style={{
                      backgroundColor: isOverdue ? '#fff5f5' : 'transparent'
                    }}>
                      <td>
                        <button
                          onClick={() => tenantNavigate(`/orders/${order.id}`)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#4299e1',
                            cursor: 'pointer',
                            fontFamily: 'monospace',
                            fontSize: '0.9rem',
                            fontWeight: 600,
                            textDecoration: 'underline',
                            padding: 0
                          }}
                        >
                          {order.name || `#${order.order_number}`}
                        </button>
                      </td>
                      <td>{format(new Date(order.order_date), 'MMM dd, yyyy')}</td>
                      <td>{order.customer_email || 'N/A'}</td>
                      <td style={{ fontWeight: 600, color: hasReturns ? '#a0aec0' : 'inherit' }}>
                        ${totalAmount.toFixed(2)}
                      </td>
                      <td style={{ color: hasReturns ? '#ed8936' : '#a0aec0' }}>
                        {hasReturns ? `-$${refundAmountAsOfDate.toFixed(2)}` : '—'}
                      </td>
                      <td style={{ fontWeight: 600, color: '#38a169' }}>
                        ${effectiveTotal.toFixed(2)}
                      </td>
                      <td>
                        <span className={`status-badge ${hasPartialPayment ? 'status-pending' : 'status-discrepancy'}`}>
                          ${paymentsAsOfDate.toFixed(2)}
                        </span>
                      </td>
                      <td style={{ color: order.eventual_paid_date ? '#38a169' : '#a0aec0' }}>
                        {order.eventual_paid_date 
                          ? format(new Date(order.eventual_paid_date), 'MMM dd, yyyy')
                          : '—'}
                      </td>
                      {showShippedColumn && (
                        <td style={{ color: shipDate ? '#38a169' : '#a0aec0' }}>
                          {shipDate ? format(shipDate, 'MMM dd, yyyy') : '—'}
                        </td>
                      )}
                      {showDaysSinceShipColumn && (
                        <td style={{
                          color: isOverdue ? '#e53e3e' : '#718096',
                          fontWeight: isOverdue ? 'bold' : 'normal'
                        }}>
                          {daysOld} days
                        </td>
                      )}
                      <td>
                        <span className={`status-badge ${
                          order.financial_status === 'paid' ? 'status-reconciled' :
                          order.financial_status === 'partially_paid' ? 'status-pending' :
                          order.financial_status === 'refunded' ? 'status-discrepancy' :
                          'status-unmapped'
                        }`}>
                          {order.financial_status || 'pending'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {allOrders.length > 0 && (
        <div className="card">
          <h2>Understanding This Report</h2>
          <div style={{ lineHeight: '1.8' }}>
            <p><strong>How it works:</strong> This report shows orders that were not fully paid <em>as of</em> the selected date. It checks:</p>
            <ul style={{ marginLeft: '1.5rem', marginTop: '0.5rem' }}>
              <li>Order was created by the selected date</li>
              <li>Payment transactions recorded up to that date</li>
              <li>Returns processed up to that date (date-aware)</li>
              <li>Excludes voided and fully refunded orders</li>
            </ul>
            <p><strong>Fulfillment Filter:</strong> Use the "Fulfillment Status" filter to see only shipped orders, only unshipped orders, or all unpaid orders regardless of fulfillment status.</p>
            <p><strong>Order Total vs Effective Total:</strong> The "Order Total" is the original order amount. "Returns by Date" shows items returned <em>as of the selected date</em> that reduce what the customer owes. "Effective Total" is what the customer actually needs to pay (Order Total - Returns by Date).</p>
            <p><strong>Paid by Date:</strong> The total amount of successful (non-voided) payment transactions recorded on or before the selected date.</p>
            <p><strong>Status:</strong> The current financial status of the order in Shopify.</p>
            {shippedFilter === 'shipped' && (
              <p style={{ marginTop: '1rem', color: '#718096' }}>
                <strong>Note:</strong> Orders shipped more than 30 days before the selected date are highlighted in red. Some of these may have been paid after the selected date.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UnpaidReport;
